import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import ChartCard from "../../components/ChartCard";
import SegmentedToggle from "../../components/SegmentedToggle";
import HorizontalBars from "../../components/charts/HorizontalBars";
import { EmptyChartNote } from "../../components/charts/StackedColumns";
import { NoteCard } from "../../components/Layout";
import { useDashboardData } from "../../hooks/useDashboardData";
import { fetchVolumeLeaderboard } from "../../dashboardApi";
import { formatCents, formatNumber } from "../../format";
import { dashboardPath } from "../../routes";
import { viz } from "../../styles";
import { VolumeLeaderboardResponse, VolumeWindow } from "../../types";

/**
 * Which customers are sending the most — the volume counterpart to the revenue
 * leaderboard on the Customers tab, and built the same way: Firestore orders and
 * limits it off `adminCustomerStats`, so a card showing ten rows costs ten rows
 * however many customers exist.
 *
 * **The board is still structurally incomplete, but far less so than it was.**
 * Only creators whose volume Twilio can actually attribute are ranked. Until
 * Sep 2026 that meant "owns a whole Twilio sub-account", which silently dropped
 * 53 of 160 paying customers whose own dedicated Markit number happens to sit on
 * the main account or inside a sub-account shared with one other creator — all
 * of them measurable, none of them measured. They are ranked now.
 *
 * What remains excluded is the one population Twilio genuinely cannot break
 * down: creators with no Markit number of their own, who send on the shared
 * number. Their `messagesSent: 0` means unknowable, not zero — ranking them
 * would put real senders below people nobody measured — so they are excluded,
 * counted, and the note under the chart gives the number.
 */
const SERIES = [{ key: "messagesSent", label: "Messages sent", color: viz.series1 }];

const TABLE_COLUMNS = [
  ...SERIES,
  { key: "spendCents", label: "Twilio spend", color: viz.series4 },
];

const WINDOWS: { key: VolumeWindow; label: string }[] = [
  { key: "30d", label: "30d" },
  { key: "90d", label: "90d" },
  { key: "lifetime", label: "All time" },
];

const TopSendersCard = () => {
  const navigate = useNavigate();
  const [window, setWindow] = useState<VolumeWindow>("30d");

  const fetcher = useCallback(() => fetchVolumeLeaderboard(window), [window]);
  const { data, error, loading, refreshing } =
    useDashboardData<VolumeLeaderboardResponse>(fetcher, [window]);

  const rows = (data?.customers ?? [])
    // A creator with no volume in the window is not a "top sender" — showing a
    // zero-length bar with a name beside it reads as a real measurement of
    // nothing rather than as an absent one.
    .filter(entry => entry.messagesSent > 0)
    .map(entry => ({
      name: entry.fullName || entry.username || entry.uid.slice(0, 8),
      uid: entry.uid,
      messagesSent: entry.messagesSent,
      spendCents: entry.spendCents,
      // Marks the spend cell, not the bar. See `hasAllocatedSpend` below.
      allocatedSpend: entry.messagingSource === "dedicatedNumber",
    }));

  // Spend IS comparable row to row now, which it was not before Sep 2026: every
  // row carries the same measurement, Twilio's real `totalprice` for the account
  // the creator's traffic was billed to. A sole-owner sub-account gets that
  // figure whole; a creator sharing an account gets their share of it, split by
  // message segments — the unit Twilio bills on.
  //
  // The dagger stays, but it now marks a much smaller distinction: allocated
  // rather than stated. It is kept because the split cannot be exact — carrier
  // fees are not published per message and Conversations is billed per monthly
  // active user — and a reader comparing two rows deserves to know which one
  // Twilio itself computed. What it no longer means is "this number is roughly
  // half of the real one".
  const hasAllocatedSpend = rows.some(row => row.allocatedSpend);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <ChartCard
        title="Top senders by messages"
        subtitle="Nightly rollup · Twilio volume per creator's own Markit number"
        series={SERIES}
        dimmed={refreshing}
        freshness={{ kind: "cached", updatedAt: data?.sync.lastRunFinishedAt ?? undefined }}
        action={
          <SegmentedToggle
            label="Window"
            options={WINDOWS}
            value={window}
            onChange={setWindow}
          />
        }
        table={{
          categoryLabel: "Customer",
          columns: TABLE_COLUMNS,
          rows: rows.map(row => ({
            category: row.name,
            values: {
              messagesSent: formatNumber(row.messagesSent),
              spendCents: `${formatCents(row.spendCents)}${row.allocatedSpend ? " †" : ""}`,
            },
          })),
        }}
      >
        {error ? (
          <EmptyChartNote label={error} />
        ) : loading ? (
          <EmptyChartNote label="Loading…" />
        ) : rows.length === 0 ? (
          <EmptyChartNote label="No attributable message volume in this window yet." />
        ) : (
          <HorizontalBars
            data={rows}
            series={SERIES}
            categoryKey="name"
            stacked={false}
            // Creator and business names run long here ("RUN IT UP EVENTS"),
            // and a clipped axis label turns one creator's name into another
            // plausible one.
            categoryWidth={110}
            valueLabelFormatter={formatNumber}
            height={Math.max(200, rows.length * 32)}
            onBarClick={row => navigate(dashboardPath.customer(row.uid))}
          />
        )}
      </ChartCard>

      {hasAllocatedSpend && (
        <NoteCard title="† Spend allocated from a shared Twilio account">
          Every row is the same measurement &mdash; Twilio&rsquo;s real total for
          the account that creator&rsquo;s traffic was billed to, carrier fees,
          number rental and Conversations included. Daggered creators share a
          Twilio account with others, so their figure is their share of that
          account&rsquo;s bill, split by message segments rather than stated by
          Twilio per creator. Checked against accounts where the true answer is
          known, the split lands within a median 6.2%, and across a whole account
          the shares add back up to the real bill.
        </NoteCard>
      )}

      {data && data.excluded > 0 && (
        <NoteCard title={`${data.excluded} creators cannot appear on this board`}>
          They have no Markit number of their own &mdash; free tier or not yet
          approved &mdash; so they send on Markit&rsquo;s shared number. Twilio
          pools that traffic and offers no per-creator breakdown, so their volume
          is unknown rather than zero, and ranking them would rank a number nobody
          measured. Everyone with their own number is ranked here, whether it sits
          in its own Twilio sub-account or on the main account. Platform-wide
          volume above is unaffected either way: it comes from one query at the
          main account, which rolls up everything.
        </NoteCard>
      )}
    </div>
  );
};

export default TopSendersCard;
